import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Clock, Plus, X, Save, Loader2, ChevronDown, Bot, ShieldAlert, CheckCircle2, XCircle } from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' }) : '—');
const STATUS_TONE = {
  COMPLETED: 'bg-emerald-100 text-emerald-800', COMPLETED_WITH_ERRORS: 'bg-amber-100 text-amber-800',
  FAILED: 'bg-red-100 text-red-800', SKIPPED_LOCKED: 'bg-slate-100 text-slate-700', RUNNING: 'bg-blue-100 text-blue-800',
};

const RunRow = ({ r }) => {
  const res = r.result || {};
  return (
    <tr className="border-t" data-testid={`automation-run-${r.run_id}`}>
      <td className="py-1.5 pr-2 font-mono text-[11px]">{r.run_id}</td>
      <td className="pr-2">{r.origin}{r.dry_run ? ' · simulação' : ''}</td>
      <td className="pr-2">{fmt(r.started_at)}</td>
      <td className="pr-2">{fmt(r.finished_at)}</td>
      <td className="pr-2"><Badge className={STATUS_TONE[r.status] || 'bg-slate-100'}>{r.status}</Badge></td>
      <td className="pr-2 text-right">{res.batches_processed ?? 0}</td>
      <td className="pr-2 text-right">{res.isl_created ?? 0} / {res.iel_created ?? 0}</td>
      <td className="pr-2 text-right">{(res.isl_reconciled ?? 0) + (res.iel_reconciled ?? 0)}</td>
      <td className="pr-2 text-right">{res.stock_issues_count ?? 0}</td>
      <td className="pr-2 text-right">{(res.errors || []).length}</td>
      <td className="text-right">{res.verification_failed ? <span className="text-red-600">{res.verification_failed} falhou</span> : (res.verification || []).length || '—'}</td>
    </tr>
  );
};

const TimesEditor = ({ times, onChange }) => {
  const [newTime, setNewTime] = useState('');
  const add = () => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(newTime)) return toast.error('Use o formato HH:MM');
    onChange([...new Set([...times, newTime])].sort());
    setNewTime('');
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="automation-times">
      {times.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm font-mono" data-testid={`automation-time-${t}`}>
          {t}
          <button onClick={() => onChange(times.filter((x) => x !== t))} data-testid={`automation-remove-${t}`} aria-label={`Remover ${t}`}>
            <X className="w-3.5 h-3.5 text-slate-500 hover:text-red-600" />
          </button>
        </span>
      ))}
      <Input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="w-28 h-8" data-testid="automation-new-time" />
      <Button size="sm" variant="outline" onClick={add} data-testid="automation-add-time"><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
    </div>
  );
};

export default function WinTouchAutomation() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/automation/wintouch/status`);
      setStatus(res.data);
      setForm({ enabled: res.data.config.enabled, times: res.data.config.times, sage_writes_enabled: res.data.config.sage_writes_enabled });
      setLoadError(null);
    } catch (e) {
      setLoadError(e.response?.data?.detail || 'Não foi possível ler o estado da automação');
    }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const save = async () => {
    if (form.sage_writes_enabled && !status.config.sage_writes_enabled
      && !window.confirm('Ativar escritas automáticas no SAGE? Os horários passam a criar ISL/IEL reais.')) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/automation/wintouch/config`, form);
      setStatus(res.data);
      toast.success('Configuração guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!status || !form) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500" data-testid="automation-loading">
        {loadError ? <span className="text-red-600" data-testid="automation-load-error">{loadError}</span> : 'A carregar automação…'}
      </div>
    );
  }
  const last = status.last_run;
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="wintouch-automation-panel">
      <button className="w-full flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-left" onClick={() => setOpen(!open)} data-testid="automation-toggle-panel">
        <div className="flex items-center gap-3">
          <Bot className="w-5 h-5 text-slate-600" />
          <span className="font-semibold text-slate-900">Integração Automática WinTouch</span>
          <Badge className={status.config.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'} data-testid="automation-state">
            {status.config.enabled ? 'Ativa' : 'Desativada'}
          </Badge>
          <Badge className={status.config.sage_writes_enabled && status.sage_writes_possible ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-600'}>
            {status.config.sage_writes_enabled && status.sage_writes_possible ? 'Escreve no SAGE' : 'Só simulação SAGE'}
          </Badge>
          {status.lock?.held && <Badge className="bg-blue-100 text-blue-800">Em curso: {status.lock.owner}</Badge>}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span data-testid="automation-next-run">Próxima: {fmt(status.next_run)}</span>
          <span data-testid="automation-last-run">Última: {last ? `${fmt(last.started_at)} · ${last.status}` : '—'}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} />
        </div>
      </button>
      {open && (
        <div className="border-t px-4 py-4 space-y-4">
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="automation-enabled-switch" />
              Ativar automatização
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.sage_writes_enabled} onCheckedChange={(v) => setForm({ ...form, sage_writes_enabled: v })} data-testid="automation-sage-writes-switch" />
              Criar ISL/IEL reais no SAGE
            </label>
            {!status.sage_writes_possible && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-700"><ShieldAlert className="w-4 h-4" />Ambiente {status.environment}: escritas SAGE bloqueadas (só simulação)</span>
            )}
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Horários diários (Europe/Lisbon · executa até 15 min após a hora)</div>
            <TimesEditor times={form.times} onChange={(times) => setForm({ ...form, times })} />
          </div>
          <Button onClick={save} disabled={saving} data-testid="automation-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar configuração
          </Button>
          {last?.result?.verification?.length > 0 && (
            <div className="text-xs space-y-1" data-testid="automation-verification">
              <div className="font-medium text-slate-600">Verificação SAGE da última execução (Sector = Armazém)</div>
              {last.result.verification.map((v) => (
                <div key={v.reference} className="flex items-center gap-2">
                  {v.verified ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-600" />}
                  <span className="font-mono">{v.reference}</span>
                  <span className="text-slate-500">sector {v.sector || '?'} · armazém {(v.warehouses || []).join(',') || '?'} · esperado {v.expected_warehouse} · data {v.sage_date || '?'} {v.error || ''}</span>
                </div>
              ))}
            </div>
          )}
          <div className="overflow-x-auto">
            <div className="text-xs font-medium text-slate-500 mb-1">Histórico de execuções</div>
            <table className="w-full text-xs" data-testid="automation-runs-table">
              <thead className="text-slate-500"><tr>
                <th className="text-left">ID</th><th className="text-left">Origem</th><th className="text-left">Início</th><th className="text-left">Fim</th>
                <th className="text-left">Resultado</th><th className="text-right">Batches</th><th className="text-right">ISL / IEL</th>
                <th className="text-right">Reconc.</th><th className="text-right">Stock Issues</th><th className="text-right">Erros</th><th className="text-right">Verif. SAGE</th>
              </tr></thead>
              <tbody>
                {status.runs.length ? status.runs.map((r) => <RunRow key={r.run_id} r={r} />)
                  : <tr><td colSpan={11} className="py-2 text-slate-400">Sem execuções automáticas registadas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
